import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import AchievementBadge from '../components/AchievementBadge';
import FriendCard from '../components/FriendCard';
import NotificationToast from '../components/NotificationToast';
import RecentActivityCard from '../components/RecentActivityCard';
import profileService from '../services/profileService';
import { PageSkeleton } from '../components/SkeletonLoader';

// Force refresh with timestamp
console.log('🔄 MyProgress component loaded at:', new Date().toISOString());

const MyProgress = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [nickname, setNickname] = useState('');
  const [avatar, setAvatar] = useState('');
  const [userStats, setUserStats] = useState({
    coins: 0,
    quizzes: 0,
    flashcards: 0,
    streak: 0,
    level: 'First Steps'
  });
  const [achievements, setAchievements] = useState([]);
  const [friends, setFriends] = useState([]);
  const [showAddFriend, setShowAddFriend] = useState(false);
  const [newFriendEmail, setNewFriendEmail] = useState('');
  const [selectedTab, setSelectedTab] = useState('overview');
  const [notification, setNotification] = useState(null);
  const [recentActivities, setRecentActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check authentication
    const userStr = localStorage.getItem('user');
    let userData = null;
    try {
      userData = userStr ? JSON.parse(userStr) : null;
    } catch (e) {
      userData = null;
    }
    if (!userData || !userData.name) {
      navigate('/login', { replace: true });
      return;
    }

    // Load profile data from backend
    loadProfileData();
    loadRecentActivities();
    
    // Set loading to false after data is loaded
    setTimeout(() => {
      setLoading(false);
    }, 2000);
  }, [navigate, user]);

  const loadProfileData = async () => {
    try {
      // Check if user is authenticated
      const userStr = localStorage.getItem('user');
      if (!userStr) {
        setNotification({ 
          message: 'Please log in to view your progress', 
          type: 'error' 
        });
        return;
      }

      const profileData = await profileService.getProfile();
      
      setNickname(profileData.nickname || '');
      setAvatar(profileData.avatar_url || '');
      
      setUserStats({
        coins: profileData.coins || 0,
        quizzes: profileData.total_quizzes || 0,
        flashcards: profileData.total_flashcards || 0,
        streak: profileData.streak || 0,
        level: profileData.level || 'First Steps'
      });

      setAchievements(profileData.achievements || []);
      setFriends(profileData.friends || []);
      
    } catch (error) {
      console.error('Error loading profile data:', error);
      
      // Check if it's an authentication error
      if (error.message.includes('401') || error.message.includes('403') || error.message.includes('Not authenticated')) {
        setNotification({ 
          message: 'Please log in to view your progress. Redirecting to login...', 
          type: 'error' 
        });
        // Redirect to login after a short delay
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        setNotification({ 
          message: 'Failed to load profile data. Please try again.', 
          type: 'error' 
        });
      }
    }
  };

  const loadRecentActivities = async () => {
    try {
      setLoadingActivities(true);
      const activities = await profileService.getRecentActivities(10);
      setRecentActivities(activities.activities || []);
    } catch (error) {
      console.error('Error loading recent activities:', error);
    } finally {
      setLoadingActivities(false);
    }
  };

  const handleAchievementClick = (achievement) => {
    setNotification({
      message: achievement.unlocked 
        ? `Achievement unlocked: ${achievement.name}` 
        : `Achievement: ${achievement.name} - ${achievement.description}`,
      type: achievement.unlocked ? 'success' : 'info'
    });
  };

  const handleAddFriend = async () => {
    if (!newFriendEmail.trim()) {
      setNotification({ message: 'Please enter a valid email', type: 'error' });
      return;
    }

    try {
      await profileService.sendFriendRequest(newFriendEmail);
      setNotification({ message: 'Friend request sent successfully!', type: 'success' });
      setNewFriendEmail('');
      setShowAddFriend(false);
      // Reload friends list
      loadProfileData();
    } catch (error) {
      setNotification({ message: 'Failed to send friend request', type: 'error' });
    }
  };

  const handleRemoveFriend = async (friendToRemove) => {
    try {
      await profileService.removeFriend(friendToRemove.id);
      setNotification({ message: 'Friend removed successfully!', type: 'success' });
      // Reload friends list
      loadProfileData();
    } catch (error) {
      setNotification({ message: 'Failed to remove friend', type: 'error' });
    }
  };

  const handleMessageFriend = (friend) => {
    setNotification({ message: `Message feature coming soon!`, type: 'info' });
  };

  const getLevelColor = (level) => {
    const colors = {
      'First Steps': 'from-green-400 to-green-600',
      'Subject Explorer': 'from-blue-400 to-blue-600',
      'Quiz Master': 'from-purple-400 to-purple-600',
      'Streak Keeper': 'from-orange-400 to-orange-600',
      'Content Creator': 'from-pink-400 to-pink-600'
    };
    return colors[level] || 'from-gray-400 to-gray-600';
  };

  const clearNotification = () => {
    setNotification(null);
  };

  // Show skeleton while loading
  if (loading) {
    return <PageSkeleton />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-lg border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(-1)}
                className="text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white transition-colors"
              >
                ← Back
              </button>
              <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
                My Progress
              </h1>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Profile Card */}
        <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl p-8 border border-gray-200 dark:border-gray-700 mb-8 transform transition-all duration-300 hover:scale-102 hover:shadow-2xl">
          <div className="flex items-center space-x-6">
            <div className="relative">
              <img
                src={avatar || profileService.getUserAvatarUrl()}
                alt="Profile"
                className="w-24 h-24 rounded-full object-cover border-4 border-white dark:border-gray-700 shadow-lg"
              />
              <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                🏆
              </div>
            </div>
            <div className="flex-1">
              <h2 className="text-3xl font-bold text-gray-800 dark:text-white mb-2">
                {nickname || 'Student'}
              </h2>
              <div className="flex items-center space-x-4">
                <span className={`px-4 py-2 rounded-full text-white font-semibold bg-gradient-to-r ${getLevelColor(userStats.level)}`}>
                  {userStats.level}
                </span>
                <span className="text-gray-600 dark:text-gray-300">
                  {userStats.streak} day streak
                </span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-yellow-500 mb-1">
                {userStats.coins}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-300">
                Coins
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl border border-gray-200 dark:border-gray-700 mb-8">
          <div className="flex space-x-1 p-2">
            {[
              { id: 'overview', label: '👤 Overview', icon: '👤' },
              { id: 'achievements', label: '🏆 Achievements', icon: '🏆' },
              { id: 'friends', label: '👥 Friends', icon: '👥' },
              { id: 'statistics', label: '📊 Statistics', icon: '📊' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setSelectedTab(tab.id)}
                className={`flex-1 py-3 px-4 rounded-2xl font-semibold transition-all duration-300 ease-in-out transform hover:scale-102 ${
                  selectedTab === tab.id
                    ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg transform scale-105'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <span className="text-lg mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="min-h-[600px] transition-all duration-300 ease-in-out">
          {selectedTab === 'overview' && (
            <div className="space-y-8 animate-fade-in">
              {/* Statistics Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="p-6 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-lg font-semibold">Quizzes</p>
                      <p className="text-sm opacity-90">Created & Completed</p>
                    </div>
                    <div className="text-right">
                      <span className="text-3xl font-bold">{userStats.quizzes}</span>
                      <p className="text-sm opacity-90">total</p>
                    </div>
                  </div>
                </div>
                <div className="p-6 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-lg font-semibold">Flashcards</p>
                      <p className="text-sm opacity-90">Created & Studied</p>
                    </div>
                    <div className="text-right">
                      <span className="text-3xl font-bold">{userStats.flashcards}</span>
                      <p className="text-sm opacity-90">total</p>
                    </div>
                  </div>
                </div>
                <div className="p-6 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-lg font-semibold">Friends</p>
                      <p className="text-sm opacity-90">Study buddies</p>
                    </div>
                    <div className="text-right">
                      <span className="text-3xl font-bold">{friends.length}</span>
                      <p className="text-sm opacity-90">total</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Recent Activity */}
              <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl p-8 border border-gray-200 dark:border-gray-700">
                <h3 className="text-2xl font-bold text-gray-800 dark:text-white mb-6 flex items-center gap-3">
                  <span className="text-3xl">🕒</span>
                  Recent Activity
                </h3>
                <div className="space-y-4">
                  {loadingActivities ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="relative overflow-hidden w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-full">
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                          initial={{ x: '-100%' }}
                          animate={{ x: '100%' }}
                          transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
                        />
                      </div>
                      <span className="ml-3 text-gray-600 dark:text-gray-400">Loading activities...</span>
                    </div>
                  ) : recentActivities.length > 0 ? (
                    recentActivities.map((activity, index) => (
                      <RecentActivityCard key={index} activity={activity} />
                    ))
                  ) : (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      <p className="text-lg">No recent activities</p>
                      <p className="text-sm">Start learning to see your activity here!</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {selectedTab === 'achievements' && (
            <div className="space-y-8 animate-fade-in">
              <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl p-8 border border-gray-200 dark:border-gray-700">
                <h3 className="text-2xl font-bold text-gray-800 dark:text-white mb-6 flex items-center gap-3">
                  <span className="text-3xl">🏆</span>
                  Achievements
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {achievements.map((achievement) => (
                    <AchievementBadge
                      key={achievement.id}
                      achievement={achievement}
                      onClick={handleAchievementClick}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          {selectedTab === 'friends' && (
            <div className="space-y-8 animate-fade-in">
              <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl p-8 border border-gray-200 dark:border-gray-700">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-3">
                    <span className="text-3xl">👥</span>
                    Friends
                  </h3>
                  <button
                    onClick={() => setShowAddFriend(true)}
                    className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-2 rounded-xl font-semibold hover:from-blue-600 hover:to-purple-700 transition-all duration-200"
                  >
                    + Add Friend
                  </button>
                </div>
                
                <div className="space-y-4">
                  {friends.map((friend) => (
                    <FriendCard
                      key={friend.id}
                      friend={friend}
                      onRemove={() => handleRemoveFriend(friend)}
                      onMessage={() => handleMessageFriend(friend)}
                    />
                  ))}
                  
                  {friends.length === 0 && (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      <p className="text-lg">No friends yet</p>
                      <p className="text-sm">Add friends to study together!</p>
                    </div>
                  )}
                </div>

                {/* Add Friend Modal */}
                {showAddFriend && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-3xl p-8 max-w-md w-full mx-4">
                      <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-4">
                        Add Friend
                      </h3>
                      <input
                        type="email"
                        placeholder="Enter friend's email"
                        value={newFriendEmail}
                        onChange={(e) => setNewFriendEmail(e.target.value)}
                        className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-800 dark:text-white mb-4"
                      />
                      <div className="flex space-x-3">
                        <button
                          onClick={handleAddFriend}
                          className="flex-1 bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-purple-700 transition-all duration-200"
                        >
                          Send Request
                        </button>
                        <button
                          onClick={() => setShowAddFriend(false)}
                          className="flex-1 bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-white py-3 rounded-xl font-semibold hover:bg-gray-400 dark:hover:bg-gray-500 transition-all duration-200"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {selectedTab === 'statistics' && (
            <div className="space-y-8 animate-fade-in">
              <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl p-8 border border-gray-200 dark:border-gray-700">
                <h3 className="text-2xl font-bold text-gray-800 dark:text-white mb-6 flex items-center gap-3">
                  <span className="text-3xl">📊</span>
                  Statistics
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="text-center p-6 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-2xl text-white">
                    <div className="text-4xl font-bold mb-2">{userStats.coins}</div>
                    <div className="text-sm opacity-90">Total Coins</div>
                  </div>
                  <div className="text-center p-6 bg-gradient-to-br from-green-400 to-emerald-500 rounded-2xl text-white">
                    <div className="text-4xl font-bold mb-2">{userStats.streak}</div>
                    <div className="text-sm opacity-90">Day Streak</div>
                  </div>
                  <div className="text-center p-6 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-2xl text-white">
                    <div className="text-4xl font-bold mb-2">{userStats.quizzes}</div>
                    <div className="text-sm opacity-90">Quizzes Completed</div>
                  </div>
                  <div className="text-center p-6 bg-gradient-to-br from-purple-400 to-pink-500 rounded-2xl text-white">
                    <div className="text-4xl font-bold mb-2">{userStats.flashcards}</div>
                    <div className="text-sm opacity-90">Flashcards Created</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Notification Toast */}
      {notification && (
        <NotificationToast
          message={notification.message}
          type={notification.type}
          onClose={clearNotification}
        />
      )}
    </div>
  );
};

export default MyProgress; 